/**
 * Secure Token Storage Utility
 * Provides secure storage options for JWT tokens with automatic cleanup
 */

interface TokenData {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope: string;
  stored_at: number;
  user_data?: any;
}

class TokenStorage {
  private readonly TOKEN_KEY = 'fintra_token_data';
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh 5 minutes before expiry
  private refreshTimer: NodeJS.Timeout | null = null;

  /**
   * Store token data securely
   */
  setTokenData(tokenData: Omit<TokenData, 'stored_at'>): void {
    const dataToStore: TokenData = {
      ...tokenData,
      stored_at: Date.now(),
    };

    try {
      // For production, consider using secure storage options:
      // 1. HttpOnly cookies (server-side implementation)
      // 2. Secure sessionStorage for short sessions
      // 3. IndexedDB with encryption
      
      if (this.isSecureContext()) {
        // Use sessionStorage for more secure, session-only storage
        sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(dataToStore));
        // Also backup in localStorage for persistence across tabs
        localStorage.setItem(this.TOKEN_KEY, JSON.stringify(dataToStore));
      } else {
        // Fallback to localStorage only
        localStorage.setItem(this.TOKEN_KEY, JSON.stringify(dataToStore));
      }

      // Automatic refresh timer removed to prevent infinite loops
    } catch (error) {
      console.error('Failed to store token data:', error);
      throw new Error('Token storage failed');
    }
  }

  /**
   * Retrieve token data
   */
  getTokenData(): TokenData | null {
    try {
      // Try sessionStorage first (more secure), then localStorage
      let dataStr = null;
      
      if (this.isSecureContext()) {
        dataStr = sessionStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY);
      } else {
        dataStr = localStorage.getItem(this.TOKEN_KEY);
      }

      if (!dataStr) return null;

      const data: TokenData = JSON.parse(dataStr);
      
      // Validate token expiry
      if (this.isTokenExpired(data)) {
        this.clearTokenData();
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to retrieve token data:', error);
      this.clearTokenData();
      return null;
    }
  }

  /**
   * Get just the access token
   */
  getAccessToken(): string | null {
    const tokenData = this.getTokenData();
    return tokenData?.access_token || null;
  }

  /**
   * Check if token is valid and not expired
   */
  isTokenValid(): boolean {
    const tokenData = this.getTokenData();
    return tokenData !== null && !this.isTokenExpired(tokenData);
  }

  /**
   * Check if token needs refresh soon
   */
  needsRefresh(): boolean {
    const tokenData = this.getTokenData();
    if (!tokenData) return false;

    const expiryTime = tokenData.stored_at + (tokenData.expires_in * 1000);
    const refreshTime = expiryTime - this.REFRESH_THRESHOLD;
    
    return Date.now() >= refreshTime;
  }

  /**
   * Clear all token data
   */
  clearTokenData(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      if (this.isSecureContext()) {
        sessionStorage.removeItem(this.TOKEN_KEY);
      }
      
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    } catch (error) {
      console.error('Failed to clear token data:', error);
    }
  }

  /**
   * Update user data in stored token
   */
  updateUserData(userData: any): void {
    const tokenData = this.getTokenData();
    if (tokenData) {
      tokenData.user_data = userData;
      this.setTokenData(tokenData);
    }
  }

  /**
   * Get stored user data
   */
  getUserData(): any | null {
    const tokenData = this.getTokenData();
    return tokenData?.user_data || null;
  }

  /**
   * Check if token is expired
   */
  private isTokenExpired(tokenData: TokenData): boolean {
    const expiryTime = tokenData.stored_at + (tokenData.expires_in * 1000);
    return Date.now() >= expiryTime;
  }

  /**
   * Check if we're in a secure context
   */
  private isSecureContext(): boolean {
    return typeof window !== 'undefined' && 
           window.isSecureContext && 
           typeof sessionStorage !== 'undefined';
  }
}

// Singleton instance
export const tokenStorage = new TokenStorage();

// Utility functions for easy access
export const getAccessToken = () => tokenStorage.getAccessToken();
export const isTokenValid = () => tokenStorage.isTokenValid();
export const needsTokenRefresh = () => tokenStorage.needsRefresh();
export const clearTokens = () => tokenStorage.clearTokenData();

// Types for external use
export type { TokenData };